import React, { useState } from "react";
import useApi from "storybook-dashboard/utils/fetching";
import { useParams } from "react-router-dom";
import Im from "immutable";
import { getAncestorPaths } from "storybook-dashboard/context/report";

const TreeContext = React.createContext();

export function useTreeContext() {
  const context = React.useContext(TreeContext);
  if (context === undefined) {
    throw new Error("useTreeContext must be used within a TreeProvider");
  }
  return context;
}

const getRootCompany = (companyId, companiesById) => {
  // Get the root company from a list of companies
  const company = companiesById?.get(companyId);
  let parentId = company?.get("parentid");
  if (!parentId) return company;
  let parent = companiesById?.get(parentId);
  if (!parent) {
    return company;
  } else {
    // RECURSE up the tree until we get to the top
    return getRootCompany(parent.get("uid"), companiesById);
  }
};

const getIds = (list) => (list || Im.List()).map((i) => i.get("uid"));

const getById = (list) => (list || Im.List()).reduce((acc, item) => acc.set(item.get("uid"), item), Im.Map());

export default function TreeProvider({ children, fromHere, ExtraContentComponent, excludeLevels }) {
  const { _companyId, _projectId, _refId } = useParams();
  const { data: companyList, trigger: triggerCompanies } = useApi("/api/company/");
  const { data: projectList, trigger: triggerProjects } = useApi("/api/project/");
  const { data: refs, trigger: triggerRefs } = useApi("/api/tracker/ref/?light=true&includeOnHold=true");
  const { data: tenant_meta } = useApi("/api/tenant/meta/");

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  const companiesById = getById(companyList);
  const projectsById = getById(projectList);
  const refsById = getById(refs);

  const getCompanyById = (id) => companiesById?.get(id);

  const refsByProjectId = (refs || Im.List()).reduce((acc, ref) => {
    let projectId = ref.get("project");
    return acc.update(projectId, Im.List(), (refs) => refs.push(ref));
  }, Im.Map());

  const refsByPath = (refs || Im.List()).reduce((acc, ref) => {
    const path = ref.get("path");
    const parentPath = path.slice(0, -7);
    return acc
      .update(path, Im.Map(), (existing) => existing.merge(ref))
      .updateIn([parentPath, "childPaths"], Im.List(), (childPaths) => childPaths.push(path));
  }, Im.Map());

  const shortToFullIdMap = getIds(companyList)
    .concat(getIds(projectList))
    .concat(getIds(refs))
    .reduce((acc, id) => acc.set(id.substring(0, 8), id), Im.Map());

  const getFullId = (shortId) => shortToFullIdMap.get(shortId?.substring(0, 8));

  if (!companyList || !projectList || !refs) {
    return <p>Loading...</p>;
  }

  let companyId = getFullId(_companyId);
  const projectId = getFullId(_projectId);
  const refId = getFullId(_refId);
  const project = projectsById?.get(projectId);
  const company = companiesById?.get(companyId) || project?.get("company");
  companyId = company?.get("uid");
  let rootCompany = fromHere ? company : getRootCompany(companyId, companiesById);

  // Get selected ref
  const selectedRef = refId ? refsById?.get(refId) : null;

  // Function to check if a company is an ancestor of the selected item
  const isAncestorOfSelected = (checkCompanyId) => {
    if (!checkCompanyId) return false;

    // If a ref is selected, check if this company contains its project
    if (selectedRef) {
      const selectedProject = projectsById?.get(selectedRef.get("project"));
      if (selectedProject) {
        const selectedProjectCompanyId = selectedProject.getIn(["company", "uid"]);
        return isCompanyAncestor(checkCompanyId, selectedProjectCompanyId);
      }
    }

    // If a project is selected, check if this company contains it
    if (project && projectId) {
      const selectedProjectCompanyId = project.getIn(["company", "uid"]);
      return isCompanyAncestor(checkCompanyId, selectedProjectCompanyId);
    }

    // If a company is selected, check if this company is its ancestor
    if (companyId) {
      return isCompanyAncestor(checkCompanyId, companyId);
    }

    return false;
  };

  // Helper function to check if companyA is an ancestor of companyB
  const isCompanyAncestor = (ancestorId, descendantId) => {
    if (!ancestorId || !descendantId) return false;
    if (ancestorId === descendantId) return true;

    const descendant = companiesById?.get(descendantId);
    const parentId = descendant?.get("parentid");

    if (!parentId) return false;
    if (parentId === ancestorId) return true;

    return isCompanyAncestor(ancestorId, parentId);
  };

  // Function to check if a project should be expanded (if it contains selected ref)
  const shouldExpandProject = (checkProjectId) => {
    if (!selectedRef || !checkProjectId) return false;
    return selectedRef.get("project") === checkProjectId;
  };

  // Function to check if a work package should be expanded (if it contains selected ref as descendant)
  const shouldExpandWorkPackage = (refPath) => {
    if (!selectedRef || !refPath) return false;
    const selectedRefPath = selectedRef.get("path");
    if (!selectedRefPath) return false;

    // Check if the selected ref path starts with the work package path (meaning it's a descendant)
    return selectedRefPath !== refPath && selectedRefPath.startsWith(refPath);
  };

  // Search filtering functions
  const searchMatches = (text, term) => {
    if (!term) return true;
    return text?.toLowerCase().includes(term.toLowerCase());
  };

  const isMatchingItem = (item, searchTerm) => {
    if (!searchTerm) return true;
    const name = item?.get("name") || "";
    return searchMatches(name, searchTerm);
  };

  const projectHasMatchingDescendant = (projectId) => {
    if (!searchTerm) return false;

    const project = projectsById?.get(projectId);
    if (!project) return false;

    // Check work packages for this project
    const projectRefs = refsByProjectId.get(project.get("uid")) || Im.List();
    return projectRefs.some((ref) => isMatchingItem(ref, searchTerm));
  };

  const companyHasMatchingDescendant = (companyId) => {
    if (!searchTerm) return false;

    const company = companiesById?.get(companyId);
    if (!company) return false;

    // Check child companies
    const childIds = company.get("childids") || Im.List();
    const hasMatchingChildren = childIds.some((childId) => {
      const child = companiesById?.get(childId);
      return child && (isMatchingItem(child, searchTerm) || companyHasMatchingDescendant(childId));
    });

    // Check projects
    const projects = company.get("projects") || Im.List();
    const hasMatchingProjects = projects.some(
      (project) => isMatchingItem(project, searchTerm) || projectHasMatchingDescendant(project.get("uid"))
    );

    return hasMatchingChildren || hasMatchingProjects;
  };

  const refHasMatchingDescendant = (refPath) => {
    if (!searchTerm) return false;

    const ref = refsByPath?.get(refPath);
    if (!ref) return false;

    // Check child refs
    const childPaths = ref.get("childPaths") || Im.List();

    return childPaths.some((path) => {
      const childRef = refsByPath?.get(path);
      return childRef && (isMatchingItem(childRef, searchTerm) || refHasMatchingDescendant(path));
    });
  };

  const hasMatchingDescendant = ({ companyId, projectId, refPath }) => {
    if (companyId) {
      return companyHasMatchingDescendant(companyId);
    } else if (projectId) {
      return projectHasMatchingDescendant(projectId);
    } else if (refPath) {
      return refHasMatchingDescendant(refPath);
    }
    return false;
  };

  // Functions to check if an item is a descendant of a matching ancestor
  const isDescendantOfMatching = (item, itemType, itemId) => {
    if (!searchTerm) return false;

    switch (itemType) {
      case "company":
        return isCompanyDescendantOfMatching(itemId);
      case "project":
        return isProjectDescendantOfMatching(itemId);
      case "ref":
        return isRefDescendantOfMatching(itemId);
      default:
        return false;
    }
  };

  const isCompanyDescendantOfMatching = (companyId) => {
    if (!searchTerm || !companyId) return false;

    const company = companiesById?.get(companyId);
    if (!company) return false;

    // Check if parent company matches search
    const parentId = company.get("parentid");
    if (parentId) {
      const parent = companiesById?.get(parentId);
      if (parent && isMatchingItem(parent, searchTerm)) {
        return true;
      }
      // Recursively check ancestors
      return isCompanyDescendantOfMatching(parentId);
    }

    return false;
  };

  const isProjectDescendantOfMatching = (projectId) => {
    if (!searchTerm || !projectId) return false;

    const project = projectsById?.get(projectId);
    if (!project) return false;

    // Check if parent company matches search
    const companyId = project.getIn(["company", "uid"]);
    if (companyId) {
      const company = companiesById?.get(companyId);
      if (company && isMatchingItem(company, searchTerm)) {
        return true;
      }
      // Check if any ancestor company matches
      return isCompanyDescendantOfMatching(companyId);
    }

    return false;
  };

  const isRefDescendantOfMatching = (refId) => {
    if (!searchTerm || !refId) return false;

    const ref = refsById?.get(refId);
    if (!ref) return false;

    // Check ancestor refs
    const ancestorPaths = getAncestorPaths(ref.get("path"));
    for (let path of ancestorPaths) {
      const ancestorRef = refsByPath.get(path);
      if (ancestorRef && isMatchingItem(ancestorRef, searchTerm)) {
        return true;
      }
    }

    // Check if parent project matches search
    const projectId = ref.get("project");
    if (projectId) {
      const project = projectsById?.get(projectId);
      if (project && isMatchingItem(project, searchTerm)) {
        return true;
      }
      // Check if any ancestor project matches
      return isProjectDescendantOfMatching(projectId);
    }

    return false;
  };

  return (
    <TreeContext.Provider
      value={{
        company,
        project,
        companyList,
        companiesById,
        projectsById,
        projectList,
        rootCompany,
        getCompanyById,
        refsByProjectId,
        refsByPath,
        tenant_meta,
        searchTerm,
        setSearchTerm,
        isMatchingItem,
        hasMatchingDescendant,
        isDescendantOfMatching,
        isAncestorOfSelected,
        shouldExpandProject,
        shouldExpandWorkPackage,
        selectedRef,
        triggerCompanies,
        triggerProjects,
        triggerRefs,
        ExtraContentComponent,
        excludeLevels,
      }}
    >
      {children}
    </TreeContext.Provider>
  );
}
